import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import './RequestEmailsModal.css';

interface RequestEmailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: any;
}

const RequestEmailsModal: React.FC<RequestEmailsModalProps> = ({ isOpen, onClose, request }) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [emailHistory, setEmailHistory] = useState<any[]>([]);
  const [allEmails, setAllEmails] = useState<any[]>([]); // Store all emails for preview
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (isOpen && request && token) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      fetchEmailHistory();
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
      
      // Reset state when modal is closed
      if (!isOpen) {
        setSuppliers([]);
        setSelectedSupplier(null);
        setEmailHistory([]);
        setAllEmails([]);
        setLoading(false);
        setError(null);
        setSidebarCollapsed(false);
      }
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, request, token]);

  // Separate useEffect to handle initial supplier selection after allEmails is populated
  useEffect(() => {
    if (allEmails.length > 0 && suppliers.length > 0 && !selectedSupplier) {
      // Find first supplier that has received messages
      const firstSupplierWithReceived = suppliers.find(supplier => 
        getSupplierPreview(supplier.supplier_name) !== null
      );
      
      if (firstSupplierWithReceived) {
        console.log('🔍 DEBUG: Auto-selecting first supplier with received messages after allEmails loaded:', firstSupplierWithReceived.supplier_name);
        setSelectedSupplier(firstSupplierWithReceived.supplier_name);
        loadSupplierEmails(firstSupplierWithReceived.supplier_name);
      }
    }
  }, [allEmails, suppliers, selectedSupplier]);

  const fetchEmailHistory = async () => {
    if (!request || !token) {
      console.log('🔍 DEBUG: fetchEmailHistory - missing request or token:', { request: !!request, token: !!token });
      return;
    }
    
    console.log('🔍 DEBUG: fetchEmailHistory called for request:', request._id || request.id);

    try {
      setLoading(true);
      setError(null);

      // For user role, we'll get suppliers from request data instead of sent emails
      // This avoids 403 error on /api/emails/sent endpoint
      const requestId = request._id || request.id;
      
      // Get request details to find suppliers
      const requestResponse = await fetch(API_ENDPOINTS.REQUEST_DETAIL(requestId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (requestResponse.ok) {
        const requestData = await requestResponse.json();
        if (requestData.success) {
          const requestDetails = requestData.data;
          
          // Extract suppliers from request data
          const suppliersList: Array<{supplier_name: string, type: string, index: number}> = [];
          
          // Add AI suppliers
          if (requestDetails.analysis_result?.ai_suppliers) {
            requestDetails.analysis_result.ai_suppliers.forEach((supplier: any, index: number) => {
              if (supplier.name) {
                suppliersList.push({
                  supplier_name: supplier.name,
                  type: 'ai_supplier',
                  index: index
                });
              }
            });
          }
          
          // Add local suppliers
          if (requestDetails.analysis_result?.local_suppliers) {
            requestDetails.analysis_result.local_suppliers.forEach((supplier: any, index: number) => {
              if (supplier.name) {
                suppliersList.push({
                  supplier_name: supplier.name,
                  type: 'local_supplier',
                  index: index
                });
              }
            });
          }
          
          console.log('🔍 DEBUG: Setting suppliers:', suppliersList);
          setSuppliers(suppliersList);

          // Don't select supplier here - let the useEffect handle it after allEmails is loaded
          if (selectedSupplier) {
            console.log('🔍 DEBUG: Using existing selected supplier:', selectedSupplier);
            await loadSupplierEmails(selectedSupplier);
          } else {
            console.log('🔍 DEBUG: No supplier selected yet, will be handled by useEffect');
            
            // Try to load all emails for this request (without supplier filter)
            console.log('🔍 DEBUG: Attempting to load all emails without supplier filter...');
            try {
              const allEmailsResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/emails/history/${requestId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              if (allEmailsResponse.ok) {
                const allEmailsData = await allEmailsResponse.json();
                console.log('🔍 DEBUG: All emails response:', allEmailsData);
                console.log('🔍 DEBUG: Response success:', allEmailsData.success);
                console.log('🔍 DEBUG: Response data:', allEmailsData.data);
                console.log('🔍 DEBUG: Data length:', allEmailsData.data?.length);
                
                if (allEmailsData.success && allEmailsData.data && allEmailsData.data.email_history && allEmailsData.data.email_history.length > 0) {
                  const emails = allEmailsData.data.email_history;
                  console.log('🔍 DEBUG: Found emails in email_history:', emails.length);
                  
                  // Store all emails for preview
                  setAllEmails(emails);
                  
                  // Extract unique supplier names from emails
                  const uniqueSuppliers = new Set<string>();
                  emails.forEach((email: any) => {
                    if (email.supplier_name) {
                      uniqueSuppliers.add(email.supplier_name);
                    }
                  });
                  
                  const emailSuppliers = Array.from(uniqueSuppliers).map((supplierName, index) => ({
                    supplier_name: supplierName,
                    type: 'email_history',
                    index: index
                  }));
                  
                  console.log('🔍 DEBUG: Found suppliers from email history:', emailSuppliers);
                  setSuppliers(emailSuppliers);
                  
                    if (emailSuppliers.length > 0) {
                      // Don't select supplier here - let the useEffect handle it after allEmails is loaded
                      console.log('🔍 DEBUG: Found suppliers from email history, will be handled by useEffect');
                  } else {
                    setEmailHistory(emails);
                  }
                } else {
                  console.log('🔍 DEBUG: No emails found in response');
                  setEmailHistory([]);
                }
              } else {
                console.log('🔍 DEBUG: Failed to get all emails:', allEmailsResponse.status);
                setEmailHistory([]);
              }
            } catch (error) {
              console.log('🔍 DEBUG: Error loading all emails:', error);
              setEmailHistory([]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
      setError('Failed to load email history');
    } finally {
      setLoading(false);
    }
  };

  const loadSupplierEmails = async (supplierName: string) => {
    try {
      setLoading(true);
      setError(null);
      setEmailHistory([]); // Clear previous emails immediately
      
      const requestId = request._id || request.id;
      console.log('🔍 DEBUG: loadSupplierEmails called with:', { requestId, supplierName });
      console.log('🔍 DEBUG: API endpoint:', API_ENDPOINTS.EMAIL_HISTORY(requestId, supplierName));
      
      const response = await fetch(API_ENDPOINTS.EMAIL_HISTORY(requestId, supplierName), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 DEBUG: loadSupplierEmails response:', data);
        if (data.success) {
          const emails = data.data.email_history || data.data;
          console.log('🔍 DEBUG: Setting email history:', emails?.length, 'emails');
          setEmailHistory(emails);
        } else {
          setError(data.message || t('components.email_modal.failed_to_load_emails'));
        }
      } else {
        setError(t('components.email_modal.failed_to_load_emails'));
      }
    } catch (error) {
      console.error('Error loading supplier emails:', error);
      setError(t('components.email_modal.failed_to_load_emails'));
    } finally {
      setLoading(false);
    }
  };


  const handleSupplierChange = (supplierName: string) => {
    setSelectedSupplier(supplierName);
    loadSupplierEmails(supplierName);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return 'No Date';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSupplierPreview = (supplierName: string) => {
    // Find messages from this supplier using allEmails
    const supplierMessages = allEmails.filter(email => 
      email.supplier_name === supplierName || 
      email.from === supplierName ||
      email.sender_email === supplierName
    );
    
    if (supplierMessages.length === 0) return '';
    
    // Check if there's at least one received message
    const hasReceivedMessage = supplierMessages.some(email => email.direction === 'received');
    if (!hasReceivedMessage) return null; // Don't show suppliers without received messages
    
    // Get first received message
    const firstReceivedMessage = supplierMessages.find(email => email.direction === 'received');
    if (!firstReceivedMessage) return '';
    
    const content = firstReceivedMessage.content || firstReceivedMessage.body || '';
    
    // Clean the content and get first few words
    const cleaned = cleanEmailHeaders(content);
    const words = cleaned.split(' ').slice(0, 8).join(' '); // First 8 words
    return words.length > 50 ? words.substring(0, 50) + '...' : words;
  };

  const cleanEmailHeaders = (content: string) => {
    if (!content) return '';
    
    // Remove all HTML except tables - use simpler approach
    let cleaned = content
      .replace(/<header[^>]*>[\s\S]*?<\/header>/gi, '')
      .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
      .replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, '')
      .replace(/<h2[^>]*>[\s\S]*?<\/h2>/gi, '')
      .replace(/<h3[^>]*>[\s\S]*?<\/h3>/gi, '')
      .replace(/<div class="header"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<div class="product-name"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<div class="tracking-info"[^>]*>[\s\S]*?<\/div>/gi, '')
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
      .replace(/<meta[^>]*>/gi, '')
      // Convert line breaks to newlines BEFORE removing other HTML
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<\/div>/gi, '\n')
      // Remove all other HTML tags except table-related ones - including closing tags
      .replace(/<(?!(table|thead|tbody|tr|th|td|\/table|\/thead|\/tbody|\/tr|\/th|\/td)\b)[^>]*>/gi, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      // Remove unwanted reply patterns
      .replace(/\d{1,2} [A-Za-zığüşöçİĞÜŞÖÇ]+ \d{4} [A-Za-zığüşöçİĞÜŞÖÇ]+ \d{1,2}:\d{2} [AP]M tarihinde [^<]*<[^>]*> şunu yazdı:/gi, '')
      .replace(/\d{1,2} [A-Za-zığüşöçİĞÜŞÖÇ]+ \d{4} [A-Za-zığüşöçİĞÜŞÖÇ]+ \d{1,2}:\d{2} tarihinde [^<]*<[^>]*> şunu yazdı:/gi, '')
      .replace(/tarihinde [^<]*<[^>]*> şunu yazdı:/gi, '')
      // Clean up excessive whitespace but preserve line breaks
      .replace(/[ \t]+/g, ' ')  // Multiple spaces/tabs to single space
      .replace(/\n[ \t]+/g, '\n')  // Remove spaces after newlines
      .replace(/[ \t]+\n/g, '\n')  // Remove spaces before newlines
      .replace(/\n{3,}/g, '\n\n')  // Multiple newlines to double newlines
      .trim();
    
    return cleaned;
  };

  const downloadAttachment = async (attachment: any) => {
    try {
      // For received emails: download from Mailgun URL first
      if (attachment.url) {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL.replace('/api', '')}${attachment.url}`);
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = attachment.filename || attachment.name || 'attachment';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          return;
        }
      }
      
      // For sent emails: download from MongoDB (supplier documents)
      if (attachment.attachment_id) {
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/supplier-products/download-document/${attachment.attachment_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = attachment.filename || attachment.name || 'attachment';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          return;
        }
      }
      
      window.alert('Attachment not found');
    } catch (error) {
      console.error('Download error:', error);
      window.alert('Failed to download attachment');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="request-emails-modal-overlay">
      <div className="request-emails-modal">
        <div className="request-emails-modal-header">
          <div className="header-left">
            <h2>{t('components.email_modal.title')}</h2>
          </div>
          <button className="request-emails-close-button" onClick={onClose}>×</button>
        </div>
        
        {sidebarCollapsed && (
          <div className="toggle-container mobile-only">
            <button 
              className="supplier-toggle-btn"
              onClick={toggleSidebar}
              title="Show Suppliers"
            >
              →
            </button>
          </div>
        )}

        <div className="request-emails-modal-content">
          <div className="conversation-layout">
            {/* Supplier Sidebar */}
            <div className={`request-emails-supplier-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
              <div className="request-emails-supplier-header">
                <span>
                  {t('components.email_modal.suppliers')} ({suppliers.filter(supplier => getSupplierPreview(supplier.supplier_name) !== null).length})
                </span>
                <button 
                  className="supplier-close-btn mobile-only"
                  onClick={toggleSidebar}
                  title="Hide Suppliers"
                >
                  ×
                </button>
              </div>
              <div className="request-emails-suppliers-list">
                {suppliers.filter(supplier => getSupplierPreview(supplier.supplier_name) !== null).map((supplier, index) => (
                  <div
                    key={index}
                    className={`request-emails-supplier-item ${selectedSupplier === supplier.supplier_name ? 'active' : ''}`}
                    onClick={() => handleSupplierChange(supplier.supplier_name)}
                  >
                    <div className="request-emails-supplier-name">
                      {supplier.supplier_name}
                    </div>
                    <div className="request-emails-supplier-preview">
                      {getSupplierPreview(supplier.supplier_name)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Conversation */}
            <div className={`request-emails-conversation ${sidebarCollapsed ? 'expanded' : ''}`}>
              {loading && !selectedSupplier ? (
                <div className="loading-message">
                  {t('common.loading')}...
                </div>
              ) : selectedSupplier && getSupplierPreview(selectedSupplier) !== null ? (
                <>
                  <div className="request-emails-conversation-header">
                    <div className="request-emails-conversation-title">
                      {request?.item || request?.product_name || 'Unknown Product'}
                    </div>
                    <div className="request-emails-conversation-subtitle">
                      {t('components.email_modal.conversation_with')} {selectedSupplier} • Request #{request?.id || request?._id}
                    </div>
                  </div>
                  
                  <div className="request-emails-conversation-messages">
                    {loading && <div className="loading">{t('common.loading')}</div>}
                    {error && <div className="error">{error}</div>}
                    {!loading && !error && emailHistory.length === 0 && (
                      <div className="no-emails">{t('components.email_modal.no_emails_found')}</div>
                    )}
                    {!loading && !error && emailHistory.length > 0 && (
                      emailHistory.map((message, index) => (
                        <div key={index} className={`request-emails-message ${message.direction || 'sent'}`}>
                          <div className="message-header">
                            <div className="message-direction">
                              {message.direction === 'sent' ? t('components.email_modal.procurement_agent') : (message.from || message.sender_email || message.from_email || t('components.email_modal.unknown'))}
                            </div>
                            <div className="message-time">
                              {formatTime(message.timestamp || message.created_at || message.sent_at)}
                            </div>
                          </div>
                          
                          {/* TO Recipients */}
                          {message.to_email && (
                            <div className="message-to">
                              <strong>TO:</strong> {message.to_email}
                            </div>
                          )}
                          
                          {/* CC Recipients */}
                          {message.cc_recipients && Array.isArray(message.cc_recipients) && message.cc_recipients.length > 0 && (
                            <div className="message-cc">
                              <strong>CC:</strong> {message.cc_recipients.join(', ')}
                            </div>
                          )}
                          
                          <div className="message-subject">
                            {message.subject || t('components.email_modal.no_subject')}
                          </div>
                          
                          <div 
                            className="message-body"
                            dangerouslySetInnerHTML={{ __html: cleanEmailHeaders(message.content || message.body || '') }}
                          />

                          {/* Attachments */}
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="request-emails-message-attachments">
                              <div className="request-emails-attachments-header">{t('components.email_modal.attachments')} ({message.attachments.length})</div>
                              {message.attachments.map((attachment: any, attIndex: number) => (
                                <div key={attIndex} className="request-emails-attachment-item">
                                  <div className="request-emails-attachment-info">
                                    <div className="request-emails-attachment-name">
                                      {attachment.filename || attachment.name || 'Unknown File'}
                                    </div>
                                    <div className="request-emails-attachment-size">
                                      {Math.round((attachment.size || attachment.file_size || 0) / 1024)} KB
                                    </div>
                                  </div>
                                  <button
                                    className="request-emails-attachment-download"
                                    onClick={() => downloadAttachment(attachment)}
                                  >
                                    {t('components.email_modal.download')}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="no-conversation">
                  {selectedSupplier ? 
                    'This supplier has no received messages' : 
                    'Select a supplier to view conversation'
                  }
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestEmailsModal;
